import { useState, useMemo } from 'react';
import { useApp } from '../../contexts/AppContext';
import { useTenant } from '../../contexts/TenantContext';

// Format UTC timestamp → local time (MYT = UTC+8 or browser local)
const fmtTs = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleString('en-MY', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

export default function ReportsPage({ setPage }) {
  const { assets, facilities, requests, checkouts, movements, antennas } = useApp();
  const { features } = useTenant();
  const f = features || {};

  const [report, setReport]   = useState('summary');
  const [exported, setExported] = useState('');

  const stats = useMemo(() => ({
    total:          assets.length,
    available:      assets.filter(a => a.status === 'Available').length,
    inUse:          assets.filter(a => a.status === 'In Use').length,
    maintenance:    assets.filter(a => a.status === 'Under Maintenance').length,
    inTransit:      assets.filter(a => a.status === 'In Transit').length,
    totalValue:     assets.reduce((s, a) => s + parseFloat(a.value || 0), 0),
    pendingReqs:    f.can_requests   !== false ? requests.filter(r => r.status === 'Pending').length   : null,
    activeCheckouts:f.can_checkout   !== false ? checkouts.filter(c => c.status === 'Out').length      : null,
    totalScans:     movements.length,
  }), [assets, requests, checkouts, movements, f]);

  // Build report nav dynamically based on features
  const REPORTS = [
    { id: 'summary',   label: '📊 Summary Report',    always: true },
    { id: 'assets',    label: '📦 Asset Inventory',    always: true },
    { id: 'checkouts', label: '🔄 Checkout Report',    feature: 'can_checkout' },
    { id: 'requests',  label: '📋 Transfer Requests',  feature: 'can_requests' },
    { id: 'deliveries',label: '🚚 Deliveries',          feature: 'can_deliveries' },
    { id: 'facility',  label: '🏭 Facility Report',     always: true },
    { id: 'rfid',      label: '📡 RFID Movements',      feature: 'can_realtime' },
  ].filter(r => r.always || f[r.feature] !== false);

  // Reset to summary if current report is now hidden
  const visibleIds = REPORTS.map(r => r.id);
  const activeReport = visibleIds.includes(report) ? report : 'summary';

  const exportCSV = (data, filename, headers) => {
    const rows = [headers.map(h => h.label), ...data.map(row => headers.map(h => JSON.stringify(row[h.key] || '')))];
    const csv  = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href = url; a.download = filename + '.csv'; a.click();
    URL.revokeObjectURL(url);
    setExported(filename); setTimeout(() => setExported(''), 2000);
  };

  const ExportBar = ({ data, filename, cols }) => (
    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
      <button className="btn" style={{ background: '#0d1520', border: '1px solid #1a2a3a', color: '#00e5a0', fontSize: 12 }}
        onClick={() => exportCSV(data, filename, cols)}>
        ⬇ Export CSV
      </button>
      <button className="btn" style={{ background: '#0d1520', border: '1px solid #1a2a3a', color: '#5b9cf6', fontSize: 12 }}
        onClick={() => { const blob = new Blob([JSON.stringify(data,null,2)],{type:'application/json'}); const url=URL.createObjectURL(blob); const a=document.createElement('a');a.href=url;a.download=filename+'.json';a.click(); setExported(filename); setTimeout(()=>setExported(''),2000); }}>
        ⬇ Export JSON
      </button>
      <button className="btn" style={{ background: '#0d1520', border: '1px solid #1a2a3a', color: '#b89de8', fontSize: 12 }}
        onClick={() => window.print()}>🖨 Print</button>
      {exported === filename && <span style={{ color: '#00e5a0', fontSize: 12, display: 'flex', alignItems: 'center' }}>✓ Exported!</span>}
    </div>
  );

  const assetCols = [
    { key:'id', label:'Asset ID' }, { key:'inventory_number', label:'Inventory No' },
    { key:'epc', label:'EPC' }, { key:'name', label:'Name' }, { key:'type', label:'Type' },
    { key:'status', label:'Status' }, { key:'current_facility_name', label:'Facility' },
  ];

  return (
    <div className="fadein">
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 22, color: '#e8edf2' }}>Reports & Export</div>
        <div style={{ fontSize: 12, color: '#7090a0', marginTop: 4 }}>Generate, view and export data reports</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 20 }}>
        {/* Report nav */}
        <div style={{ background: '#0d1520', border: '1px solid #1a2a3a', borderRadius: 12, padding: 10, height: 'fit-content' }}>
          {REPORTS.map(r => (
            <button key={r.id} onClick={() => setReport(r.id)} className="nav-btn" style={{
              background: activeReport === r.id ? '#00e5a015' : 'none',
              color:      activeReport === r.id ? '#00e5a0'   : '#7090a0',
              borderLeft: activeReport === r.id ? '2px solid #00e5a0' : '2px solid transparent',
              fontSize: 12,
            }}>{r.label}</button>
          ))}
        </div>

        {/* Report content */}
        <div>
          {/* SUMMARY */}
          {activeReport === 'summary' && (
            <div>
              <ExportBar data={[stats]} filename="wms_summary" cols={Object.keys(stats).map(k => ({ key: k, label: k }))} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 20 }}>
                <StatCard label="Total Assets"   value={stats.total}      icon="📦" accent="#00e5a0" sub="All registered" />
                <StatCard label="Available"      value={stats.available}  icon="✅" accent="#00e5a0" sub={`${((stats.available/stats.total||0)*100).toFixed(0)}% of total`} />
                <StatCard label="In Use"         value={stats.inUse}      icon="📤" accent="#5b9cf6" sub="Currently out" />
                <StatCard label="Maintenance"    value={stats.maintenance} icon="🔧" accent="#f5a623" sub="Under repair" />
                <StatCard label="Total Value"    value={`RM ${(stats.totalValue/1000).toFixed(1)}K`} icon="💰" accent="#b89de8" sub="Estimated" />
                <StatCard label="RFID Scans"     value={stats.totalScans} icon="📡" accent="#00e5a0" sub="All movements" />
                {stats.pendingReqs !== null && <StatCard label="Pending Requests" value={stats.pendingReqs} icon="⏳" accent="#f5a623" sub="Awaiting approval" />}
                {stats.activeCheckouts !== null && <StatCard label="Active Checkouts" value={stats.activeCheckouts} icon="🔄" accent="#5b9cf6" sub="Currently out" />}
              </div>
              <div className="card">
                <div style={{ fontSize: 11, color: '#7090a0', textTransform: 'uppercase', letterSpacing: '1.2px', fontWeight: 600, marginBottom: 16 }}>Assets by Facility</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead><tr style={{ borderBottom: '1px solid #1a2a3a' }}>
                    {['Facility','Type','Total','Available','In Use','Maintenance'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, color: '#7090a0', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {facilities.map(fac => {
                      const fa = assets.filter(a => a.current_facility === fac.id || a.currentFacility === fac.id);
                      return (
                        <tr key={fac.id} style={{ borderBottom: '1px solid #1a2a3a' }}>
                          <td style={{ padding: '10px 12px', fontWeight: 600, color: '#e8edf2' }}>{fac.name}</td>
                          <td style={{ padding: '10px 12px', color: '#a0b4c0', fontSize: 12 }}>{fac.type || '—'}</td>
                          <td style={{ padding: '10px 12px', fontFamily: "'IBM Plex Mono',monospace" }}>{fa.length}</td>
                          <td style={{ padding: '10px 12px', color: '#00e5a0', fontFamily: "'IBM Plex Mono',monospace" }}>{fa.filter(a => a.status === 'Available').length}</td>
                          <td style={{ padding: '10px 12px', color: '#5b9cf6', fontFamily: "'IBM Plex Mono',monospace" }}>{fa.filter(a => a.status === 'In Use').length}</td>
                          <td style={{ padding: '10px 12px', color: '#f5a623', fontFamily: "'IBM Plex Mono',monospace" }}>{fa.filter(a => a.status === 'Under Maintenance').length}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ASSETS */}
          {activeReport === 'assets' && (
            <div>
              <ExportBar data={assets} filename="wms_assets" cols={assetCols} />
              <SimpleTable rows={assets} cols={[
                { key: 'inventory_number', label: 'Inventory No', render: v => <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, color: '#f5a623' }}>{v}</span> },
                { key: 'name',  label: 'Asset', render: v => <span style={{ fontWeight: 600, color: '#e8edf2' }}>{v}</span> },
                { key: 'type',  label: 'Type',  render: v => <span style={{ fontSize: 12 }}>{v}</span> },
                { key: 'status', label: 'Status', render: v => <StatusBadge status={v} /> },
                { key: 'current_facility_name', label: 'Facility', render: v => <span style={{ fontSize: 12, color: '#a0b4c0' }}>{v || '—'}</span> },
                { key: 'epc',   label: 'EPC',   render: v => <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, color: '#7090a0' }}>{v}</span> },
              ]} />
            </div>
          )}

          {/* CHECKOUTS */}
          {activeReport === 'checkouts' && f.can_checkout !== false && (
            <div>
              <ExportBar data={checkouts} filename="wms_checkouts" cols={[
                {key:'id',label:'ID'},{key:'asset_name',label:'Asset'},{key:'checked_out_by',label:'By'},
                {key:'checked_out_to',label:'Purpose'},{key:'checked_out_at',label:'Out At'},{key:'returned_at',label:'Returned'},{key:'status',label:'Status'},
              ]} />
              <SimpleTable rows={checkouts} cols={[
                { key: 'asset_name',      label: 'Asset',     render: v => <span style={{ fontWeight: 600, color: '#e8edf2' }}>{v}</span> },
                { key: 'checked_out_by',  label: 'By',        render: v => <span style={{ fontSize: 12 }}>{v}</span> },
                { key: 'checked_out_to',  label: 'Purpose',   render: v => <span style={{ fontSize: 12, color: '#c8d6e0' }}>{v}</span> },
                { key: 'checked_out_at',  label: 'Out At',    render: v => <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, color: '#7090a0' }}>{fmtTs(v)}</span> },
                { key: 'returned_at',     label: 'Returned',  render: v => <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, color: '#00e5a0' }}>{fmtTs(v) || '—'}</span> },
                { key: 'status',          label: 'Status',    render: v => <StatusBadge status={v} /> },
              ]} />
            </div>
          )}

          {/* REQUESTS */}
          {activeReport === 'requests' && f.can_requests !== false && (
            <div>
              <ExportBar data={requests} filename="wms_requests" cols={[
                {key:'id',label:'ID'},{key:'asset_name',label:'Asset'},{key:'from_facility_name',label:'From'},
                {key:'to_facility_name',label:'To'},{key:'status',label:'Status'},{key:'requested_by',label:'By'},
              ]} />
              <SimpleTable rows={requests} cols={[
                { key: 'asset_name',        label: 'Asset',    render: v => <span style={{ fontWeight: 600, color: '#e8edf2' }}>{v}</span> },
                { key: 'from_facility_name',label: 'From',     render: v => <span style={{ fontSize: 12, color: '#a0b4c0' }}>{v}</span> },
                { key: 'to_facility_name',  label: 'To',       render: v => <span style={{ fontSize: 12, color: '#c8d6e0' }}>{v}</span> },
                { key: 'priority',          label: 'Priority', render: v => <span style={{ fontWeight: 600, fontSize: 12, color: v==='Urgent'?'#e05757':v==='High'?'#f5a623':'#5b9cf6' }}>{v}</span> },
                { key: 'status',            label: 'Status',   render: v => <StatusBadge status={v} /> },
                { key: 'requested_by',      label: 'By',       render: v => <span style={{ fontSize: 12 }}>{v}</span> },
              ]} />
            </div>
          )}

          {/* DELIVERIES */}
          {activeReport === 'deliveries' && f.can_deliveries !== false && (
            <div>
              <ExportBar data={[]} filename="wms_deliveries" cols={[{key:'id',label:'ID'}]} />
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#7090a0' }}>No delivery records yet.</div>
            </div>
          )}

          {/* FACILITY */}
          {activeReport === 'facility' && (
            <div>
              <ExportBar data={facilities} filename="wms_facilities" cols={[
                {key:'id',label:'ID'},{key:'name',label:'Name'},{key:'type',label:'Type'},{key:'status',label:'Status'},
              ]} />
              <SimpleTable rows={facilities} cols={[
                { key: 'name',   label: 'Facility', render: v => <span style={{ fontWeight: 600, color: '#e8edf2' }}>{v}</span> },
                { key: 'type',   label: 'Type',     render: v => <span style={{ fontSize: 12 }}>{v || '—'}</span> },
                { key: 'status', label: 'Status',   render: v => <StatusBadge status={v} /> },
                { key: 'id',     label: 'Assets',   render: (_, row) => {
                  const count = assets.filter(a => a.current_facility === row.id || a.currentFacility === row.id).length;
                  return <span style={{ fontFamily: "'IBM Plex Mono',monospace", color: '#00e5a0' }}>{count}</span>;
                }},
              ]} />
            </div>
          )}

          {/* RFID */}
          {activeReport === 'rfid' && f.can_realtime !== false && (
            <div>
              <ExportBar data={movements.slice(0, 500)} filename="wms_rfid_movements" cols={[
                {key:'asset_name',label:'Asset'},{key:'epc',label:'EPC'},{key:'to_facility_name',label:'Facility'},
                {key:'reader_name',label:'Reader'},{key:'antenna_port',label:'Port'},{key:'rssi',label:'RSSI'},{key:'detected_at',label:'Time'},
              ]} />
              <SimpleTable rows={movements.slice(0, 200)} cols={[
                { key: 'asset_name',       label: 'Asset',    render: v => <span style={{ fontWeight: 600, color: '#e8edf2' }}>{v || '—'}</span> },
                { key: 'epc',              label: 'EPC',      render: v => <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, color: '#5b9cf6' }}>{v}</span> },
                { key: 'to_facility_name', label: 'Facility', render: v => <span style={{ fontSize: 12, color: '#a0b4c0' }}>{v || '—'}</span> },
                { key: 'reader_name',      label: 'Reader',   render: v => <span style={{ fontSize: 11, color: '#c8d6e0' }}>{v || '—'}</span> },
                { key: 'rssi',             label: 'RSSI',     render: v => <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, color: '#b89de8' }}>{v ? `${v} dBm` : '—'}</span> },
                { key: 'detected_at',      label: 'Time',     render: v => <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, color: '#7090a0' }}>{fmtTs(v)}</span> },
              ]} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Mini helpers ───────────────────────────────────────────────────── */
function StatCard({ label, value, sub, accent = '#00e5a0', icon }) {
  return (
    <div className="card" style={{ position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, width: 3, height: '100%', background: accent, borderRadius: '12px 0 0 12px' }} />
      <div style={{ fontSize: 11, color: '#7090a0', textTransform: 'uppercase', letterSpacing: '1.2px', fontWeight: 600, marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 28, fontWeight: 800, color: '#e8edf2' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#7090a0', marginTop: 5 }}>{sub}</div>}
      <div style={{ position: 'absolute', top: 16, right: 16, fontSize: 22, opacity: 0.12 }}>{icon}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = { Available:'#00e5a0', 'In Use':'#5b9cf6', 'Under Maintenance':'#f5a623', 'In Transit':'#b89de8', Pending:'#f5a623', Approved:'#00e5a0', Rejected:'#e05757', Completed:'#5b9cf6', Active:'#00e5a0', Out:'#f5a623', Returned:'#00e5a0' };
  const c = colors[status] || '#a0b4c0';
  return <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:600, background:c+'18', color:c, border:`1px solid ${c}38` }}><span style={{ width:5, height:5, borderRadius:'50%', background:c, display:'inline-block' }}/>{status}</span>;
}

function SimpleTable({ rows, cols }) {
  if (!rows || rows.length === 0) {
    return <div style={{ textAlign: 'center', padding: '40px 0', color: '#7090a0' }}>No records found</div>;
  }
  return (
    <div style={{ background: '#090e15', border: '1px solid #1a2a3a', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#060b10', borderBottom: '1px solid #1a2a3a' }}>
              {cols.map(c => <th key={c.key} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 10, color: '#7090a0', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 200).map((row, ri) => (
              <tr key={row.id || ri} style={{ borderBottom: '1px solid #0d1520', background: ri % 2 === 0 ? '#090e15' : '#060b10' }}>
                {cols.map(c => (
                  <td key={c.key} style={{ padding: '10px 12px', fontSize: 13 }}>
                    {c.render ? c.render(row[c.key], row) : <span style={{ color: '#8a9aaa' }}>{row[c.key] || '—'}</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ padding: '10px 16px', borderTop: '1px solid #1a2a3a', fontSize: 11, color: '#7090a0', fontFamily: "'IBM Plex Mono',monospace" }}>
        {rows.length} records {rows.length > 200 ? '(showing first 200)' : ''}
      </div>
    </div>
  );
}
